<template>
  <div class="debug-visualizer mt-4">
    <h3 class="text-lg font-semibold mb-2">Debug Visualization</h3>

    <!-- Strategy information -->
    <div class="bg-gray-100 p-4 mb-4 rounded">
      <h4 class="font-medium">Strategy: {{ result.strategyName }}</h4>
      <p>Waste: {{ result.wastePercentage.toFixed(2) }}%</p>
      <p>Sheets used: {{ result.usedMaterials.length }}</p>
    </div>

    <!-- Debug steps timeline -->
    <div class="mb-4">
      <h4 class="font-medium mb-2">Debug Steps:</h4>
      <div class="bg-gray-100 p-4 rounded max-h-96 overflow-auto">
        <div
          v-for="(step, index) in result.debugSteps"
          :key="`step-${index}`"
          class="border-l-4 p-2 mb-2"
          :class="getStepBorderClass(step.type)"
        >
          <p class="font-medium">{{ getStepTitle(step.type) }}</p>
          <template v-if="step.type !== 'freeRectangles'">
            <p v-if="step.part">
              Part: {{ step.part.id }} ({{ step.part.width }}x{{
                step.part.height
              }})
            </p>
            <p v-if="step.position">
              Position: ({{ step.position.x }}, {{ step.position.y }})
            </p>
          </template>
          <p v-if="step.reason" class="text-sm text-gray-700">
            {{ step.reason }}
          </p>

          <!-- Free Rectangles Visualization -->
          <div
            v-if="step.type === 'freeRectangles' && step.freeRectangles"
            class="mt-2"
          >
            <div class="flex justify-between">
              <p>Free rectangles count: {{ step.freeRectangles.length }}</p>
              <p v-if="step.placedParts">
                Placed parts: {{ step.placedParts.length }}
              </p>
            </div>
            <canvas
              :id="step.visualizationId"
              ref="canvas"
              class="border mt-2"
              width="400"
              height="300"
            >
            </canvas>
            <div class="mt-2 md:flex">
              <!-- Free rectangles info -->
              <div class="md:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <h5 class="font-medium text-xs col-span-full">
                  Free Rectangles:
                </h5>
                <div
                  v-for="(rect, rectIndex) in step.freeRectangles"
                  :key="`rect-${rectIndex}`"
                  class="text-xs flex items-center"
                >
                  <span
                    class="inline-block w-3 h-3 mr-1"
                    :style="{
                      backgroundColor:
                        getPartColor((rectIndex % 10) + 10) + '60',
                    }"
                  >
                  </span>
                  #{{ rectIndex }}: ({{ rect.x }},{{ rect.y }})
                  {{ rect.width }}x{{ rect.height }}
                </div>
              </div>

              <!-- Placed parts info -->
              <div
                v-if="step.placedParts && step.placedParts.length > 0"
                class="md:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 md:mt-0"
              >
                <h5 class="font-medium text-xs col-span-full">Placed Parts:</h5>
                <div
                  v-for="(part, partIndex) in step.placedParts"
                  :key="`part-${partIndex}`"
                  class="text-xs flex items-center"
                >
                  <span
                    class="inline-block w-3 h-3 mr-1"
                    :style="{ backgroundColor: getPartColor(partIndex) }"
                  >
                  </span>
                  {{ part.id }}: ({{ part.x || 0 }},{{ part.y || 0 }})
                  {{ part.width }}x{{ part.height }}
                  {{ part.rotated ? '(R)' : '' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Current state visualization with animation -->
    <div
      v-for="(material, materialIndex) in result.usedMaterials"
      :key="`material-${materialIndex}`"
      class="mb-8"
    >
      <h4 class="font-medium mb-2">Sheet {{ materialIndex + 1 }}</h4>

      <!-- Visual representation -->
      <div
        class="relative border border-gray-400 bg-white"
        :style="{
          width: `${material.width * effectiveScale}px`,
          height: `${material.height * effectiveScale}px`,
        }"
      >
        <!-- Parts -->
        <div
          v-for="(part, partIndex) in material.children || []"
          :key="`part-${partIndex}`"
          class="absolute flex items-center justify-center border border-gray-700 transition-all duration-500"
          :style="{
            left: `${(part.x || 0) * effectiveScale}px`,
            top: `${(part.y || 0) * effectiveScale}px`,
            width: `${part.width * effectiveScale}px`,
            height: `${part.height * effectiveScale}px`,
            backgroundColor: getPartColor(partIndex),
            transform: part.rotated ? 'rotate(0deg)' : 'rotate(0deg)', // Visual indicator for rotation
          }"
        >
          <div class="text-xs text-white font-bold">
            {{ part.id }} {{ part.rotated ? '(R)' : '' }}
          </div>
        </div>

        <!-- Free space indicators (optional, would need to reconstruct from steps) -->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch, onMounted, nextTick } from 'vue';
  import type { ICutResult } from '~/types/CutResult';
  import type { DebugStep } from '~/types/Material';

  const props = defineProps<{
    result: ICutResult;
    scale?: number;
  }>();

  // Default scale if not provided
  const effectiveScale = computed(() => props.scale || 3);

  // Draw free rectangles on canvas
  function drawFreeRectangles() {
    nextTick(() => {
      if (!props.result.debugSteps) return;

      props.result.debugSteps.forEach((step) => {
        if (
          step.type === 'freeRectangles' &&
          step.freeRectangles &&
          step.visualizationId
        ) {
          const canvas = document.getElementById(
            step.visualizationId
          ) as HTMLCanvasElement;
          if (!canvas) return;

          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          // Clear canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // Find maximum dimensions
          let maxWidth = 0;
          let maxHeight = 0;

          step.freeRectangles.forEach((rect) => {
            maxWidth = Math.max(maxWidth, rect.x + rect.width);
            maxHeight = Math.max(maxHeight, rect.y + rect.height);
          });

          // Set scaling to fit
          const scaleX = canvas.width / (maxWidth || 1000);
          const scaleY = canvas.height / (maxHeight || 1000);
          const scale = Math.min(scaleX, scaleY) * 0.9;

          // Draw the material outline
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 2;
          if (props.result.usedMaterials[0]) {
            const mat = props.result.usedMaterials[0];
            ctx.strokeRect(0, 0, mat.width * scale, mat.height * scale);
          }

          // Determine which material/sheet we're currently working with
          // This is a basic heuristic: if the step's visualization ID contains a number,
          // we use it to identify which sheet's parts to show
          const stepIndex = props.result.debugSteps
            ? props.result.debugSteps.findIndex(
                (s) => s.visualizationId === step.visualizationId
              )
            : 0;

          // Find all debug steps with "Initial free rectangle" to determine sheet boundaries
          const sheetStartIndices: number[] = [];
          if (props.result.debugSteps) {
            props.result.debugSteps.forEach((s, i) => {
              if (s.reason === 'Initial free rectangle') {
                sheetStartIndices.push(i);
              }
            });
          }

          // Determine current sheet index
          let currentSheetIndex = 0;
          for (let i = 0; i < sheetStartIndices.length; i++) {
            if (i < sheetStartIndices.length - 1) {
              // If this step index is between this start and next start, it belongs to this sheet
              const nextIndex = i + 1;
              if (
                stepIndex >= sheetStartIndices[i] &&
                stepIndex < sheetStartIndices[nextIndex]
              ) {
                currentSheetIndex = i;
                break;
              }
            } else {
              // If after the last start index, it belongs to the last sheet
              if (stepIndex >= sheetStartIndices[i]) {
                currentSheetIndex = i;
              }
            }
          }

          // Get the IDs of parts that belong to the current sheet
          const currentSheetPartIds = new Set<string>();

          // If this is the first sheet (index 0), we want to show all parts up to this point
          // This is because free rectangles visualizations for the first sheet should show all parts
          if (currentSheetIndex === 0) {
            // For the first sheet, show all parts currently placed
            if (step.placedParts) {
              step.placedParts.forEach((part) => {
                currentSheetPartIds.add(part.id);
              });
            }
          } else {
            // For subsequent sheets, only show parts that belong to this sheet
            if (currentSheetIndex < props.result.usedMaterials.length) {
              const currentMaterial =
                props.result.usedMaterials[currentSheetIndex];
              if (currentMaterial && currentMaterial.children) {
                currentMaterial.children.forEach((part) => {
                  currentSheetPartIds.add(part.id);
                });
              }
            }
          }

          // Draw placed parts first (if available)
          if (step.placedParts && step.placedParts.length > 0) {
            // Filter parts that should be shown on this sheet
            const partsToDisplay = step.placedParts.filter((part) => {
              // For the first sheet, we show all parts
              // For other sheets, we only show parts that belong to this sheet
              return currentSheetPartIds.has(part.id);
            });

            partsToDisplay.forEach((part, index) => {
              // Draw placed parts with a solid color
              ctx.fillStyle = getPartColor(index);
              ctx.strokeStyle = '#000';
              ctx.lineWidth = 1;

              const x = (part.x || 0) * scale;
              const y = (part.y || 0) * scale;
              const width = part.width * scale;
              const height = part.height * scale;

              ctx.fillRect(x, y, width, height);
              ctx.strokeRect(x, y, width, height);

              // Label the part
              ctx.fillStyle = '#fff';
              ctx.font = '10px Arial';
              ctx.fillText(
                `${part.id}${part.rotated ? '(R)' : ''}`,
                x + 5,
                y + 12
              );
            });
          }

          // Draw each free rectangle
          step.freeRectangles.forEach((rect, index) => {
            ctx.fillStyle = getPartColor((index % 10) + 10) + '60'; // Add 60 for opacity and offset colors
            ctx.strokeStyle = getPartColor((index % 10) + 10);
            ctx.lineWidth = 1;

            const x = rect.x * scale;
            const y = rect.y * scale;
            const width = rect.width * scale;
            const height = rect.height * scale;

            ctx.fillRect(x, y, width, height);
            ctx.strokeRect(x, y, width, height);

            // Label the rectangle
            ctx.fillStyle = '#000';
            ctx.font = '10px Arial';
            ctx.fillText(`${index}`, x + 5, y + 12);
          });
        }
      });
    });
  }

  // Initialize drawings on mount and update on changes
  onMounted(() => {
    drawFreeRectangles();
  });

  watch(
    () => props.result.debugSteps,
    () => {
      drawFreeRectangles();
    },
    { deep: true }
  );

  // Get a color based on part index
  function getPartColor(index: number): string {
    const colors = [
      '#3498db',
      '#2ecc71',
      '#e74c3c',
      '#f1c40f',
      '#9b59b6',
      '#1abc9c',
      '#e67e22',
      '#34495e',
      '#16a085',
      '#d35400',
    ];
    return colors[index % colors.length];
  }

  // Get class for step type
  function getStepBorderClass(type: string): string {
    switch (type) {
      case 'place':
        return 'border-green-500';
      case 'rotate':
        return 'border-blue-500';
      case 'check':
        return 'border-yellow-500';
      case 'reject':
        return 'border-red-500';
      case 'freeRectangles':
        return 'border-purple-500';
      default:
        return 'border-gray-500';
    }
  }

  // Get title for step type
  function getStepTitle(type: string): string {
    switch (type) {
      case 'place':
        return 'Placement';
      case 'rotate':
        return 'Rotation';
      case 'check':
        return 'Checking Fit';
      case 'reject':
        return 'Rejection';
      case 'freeRectangles':
        return 'Free Rectangles';
      default:
        return 'Unknown Step';
    }
  }
</script>

<style scoped>
  .debug-visualizer {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
      Roboto, sans-serif;
  }
</style>
